import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {Button, Card, DatePicker, Form, Input, message, Select, Space, Typography} from "antd";
import {ArrowLeftOutlined, SaveOutlined} from "@ant-design/icons";
import {createGroup, updateGroup, getGroupById} from "../../../../../store/slices/MicroqualificationGroupsSlice";
import {getAllMicroQualifications} from "../../../../../store/slices/MicroqualificationsSlice";
import {getAllTeachersPageable} from "../../../../../store/slices/TeachersListSlice";
import {LocalName} from "../../../../../utils/LocalName";
import dayjs from "dayjs";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";

const {Title} = Typography;

const GroupForm = ({isEdit = false}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {groupId} = useParams();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const {currentGroup, groupsLoading} = useSelector(state => state.microqualificationGroups);
    const {microQualifications, isLoading: microQualificationsLoading} = useSelector(state => state.microQualificationList);
    const {teachers, isLoading: teachersLoading} = useSelector(state => state.teachersList);

    useEffect(() => {
        dispatch(getAllMicroQualifications({page: 1, limit: 1000, role: 'admin'}));
        dispatch(getAllTeachersPageable({page: 1, limit: 1000}));
    }, [dispatch]);

    useEffect(() => {
        if (isEdit && groupId) {
            dispatch(getGroupById(groupId));
        }
    }, [isEdit, groupId, dispatch]);

    useEffect(() => {
        if (isEdit && currentGroup) {
            form.setFieldsValue({
                name: currentGroup.name,
                academicYear: currentGroup.academicYear,
                startDate: dayjs(currentGroup.startDate),
                endDate: dayjs(currentGroup.endDate),
                microqualificationId: currentGroup.microqualification?.id,
                teacherId: currentGroup.teacher?.id
            });
        }
    }, [currentGroup, form, isEdit]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const groupData = {
                name: values.name,
                academicYear: values.academicYear,
                startDate: values.startDate.format('YYYY-MM-DD'),
                endDate: values.endDate.format('YYYY-MM-DD'),
                microqualificationId: values.microqualificationId,
                teacherId: values.teacherId
            };

            if (isEdit) {
                await dispatch(updateGroup({groupId, groupData})).unwrap();
                message.success(<IntlMessage id="mqGroups.groupUpdated"/>);
            } else {
                const result = await dispatch(createGroup(groupData)).unwrap();
                message.success(<IntlMessage id="mqGroups.groupCreated"/>);
                navigate(`/p/microqualification-groups/${result.id}`);
            }
        } catch (error) {
            message.error(error?.message || <IntlMessage id={isEdit ? 'mqGroups.failedToUpdate' : 'mqGroups.failedToCreate'}/>);
        } finally {
            setLoading(false);
        }
    };

    const generateAcademicYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = -2; i <= 2; i++) {
            const startYear = currentYear + i;
            const endYear = startYear + 1;
            years.push({
                value: `${startYear}-${endYear}`,
                label: `${startYear}-${endYear}`
            });
        }
        return years;
    };

    return (
        <div style={{width: '100%', height: '100%'}}>
            <Card bordered={false}>
                <Space direction="vertical" size="large" style={{width: '100%'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                        <Button
                            icon={<ArrowLeftOutlined/>}
                            onClick={() => navigate('/p/microqualification-groups')}
                        >
                            <IntlMessage id="back"/>
                        </Button>
                        <Title level={3} style={{margin: 0}}>
                            {isEdit ? <IntlMessage id="mqGroups.editGroup"/> : <IntlMessage id="mqGroups.createNewGroup"/>}
                        </Title>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        style={{maxWidth: 800}}
                    >
                        <Form.Item
                            label={<IntlMessage id="mqGroups.groupName"/>}
                            name="name"
                            rules={[
                                {required: true, message: <IntlMessage id="mqGroups.groupNameRequired"/>},
                                {max: 100, message: <IntlMessage id="mqGroups.groupNameMax"/>}
                            ]}
                        >
                            <Input placeholder={<IntlMessage id="mqGroups.groupNamePlaceholder"/>} size="large"/>
                        </Form.Item>

                        <Form.Item
                            label={<IntlMessage id="mqGroups.microqualification"/>}
                            name="microqualificationId"
                            rules={[{required: true, message: <IntlMessage id="mqGroups.microqualificationRequired"/>}]}
                        >
                            <Select
                                placeholder={<IntlMessage id="mqGroups.selectMicroqualification"/>}
                                size="large"
                                loading={microQualificationsLoading}
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())
                                }
                            >
                                {microQualifications?.map(mq => (
                                    <Select.Option
                                        key={mq.id}
                                        value={mq.id}
                                        label={LocalName.getName(mq.name)}
                                    >
                                     {LocalName.getName(mq.name)}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label={<IntlMessage id="mqGroups.teacher"/>}
                            name="teacherId"
                            rules={[{required: true, message: <IntlMessage id="mqGroups.teacherRequired"/>}]}
                        >
                            <Select
                                placeholder={<IntlMessage id="mqGroups.selectTeacher"/>}
                                size="large"
                                loading={teachersLoading}
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label?.toLowerCase() ?? '').includes(input.toLowerCase())
                                }
                            >
                                {teachers?.map(teacher => (
                                    <Select.Option
                                        key={teacher.id}
                                        value={teacher.id}
                                        label={`${teacher.firstName} ${teacher.lastName}`}
                                    >
                                        <div>
                                            <div>{teacher.firstName} {teacher.lastName}</div>
                                            <div style={{fontSize: 12, color: '#666'}}>
                                                {teacher.email}
                                            </div>
                                        </div>
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label={<IntlMessage id="mqGroups.academicYear"/>}
                            name="academicYear"
                            rules={[{required: true, message: <IntlMessage id="mqGroups.academicYearRequired"/>}]}
                        >
                            <Select
                                placeholder={<IntlMessage id="mqGroups.selectAcademicYear"/>}
                                size="large"
                                options={generateAcademicYears()}
                            />
                        </Form.Item>

                        <Form.Item
                            label={<IntlMessage id="mqGroups.startDate"/>}
                            name="startDate"
                            rules={[{required: true, message: <IntlMessage id="mqGroups.startDateRequired"/>}]}
                        >
                            <DatePicker
                                style={{width: '100%'}}
                                size="large"
                                format="DD.MM.YYYY"
                            />
                        </Form.Item>

                        <Form.Item
                            label={<IntlMessage id="mqGroups.endDate"/>}
                            name="endDate"
                            rules={[
                                {required: true, message: <IntlMessage id="mqGroups.endDateRequired"/>},
                                ({getFieldValue}) => ({
                                    validator(_, value) {
                                        const startDate = getFieldValue('startDate');
                                        if (!value || !startDate || value.isAfter(startDate)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(<IntlMessage id="mqGroups.endDateMustBeAfter"/>));
                                    },
                                }),
                            ]}
                        >
                            <DatePicker
                                style={{width: '100%'}}
                                size="large"
                                format="DD.MM.YYYY"
                            />
                        </Form.Item>

                        <Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SaveOutlined/>}
                                    loading={loading || groupsLoading}
                                    size="large"
                                >
                                    {isEdit ? <IntlMessage id="mqGroups.updateGroup"/> : <IntlMessage id="mqGroups.createGroup"/>}
                                </Button>
                                <Button
                                    onClick={() => navigate('/p/microqualification-groups')}
                                    size="large"
                                >
                                    <IntlMessage id="cancel"/>
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Space>
            </Card>
        </div>
    );
};

export default GroupForm;
